/**
 * Feature flag configuration for AWS Amplify
 * Automatically detects environment based on Amplify environment variables
 */

/**
 * @typedef {Object} FeatureFlags
 * @property {boolean} emailFormsEnabled - Enable actual email form submission
 * @property {boolean} activeCampaignIntegration - Enable ActiveCampaign integration
 * @property {boolean} learningModeEnabled - Show "Learn More" vs "Buy Now" buttons
 * @property {boolean} debugMode - Show debug information
 * @property {boolean} previewBannerEnabled - Show preview branch banner
 * @property {boolean} googleAnalyticsEnabled - Enable GA tracking
 * @property {boolean} marketplaceStandalonePagesEnabled - Enable standalone marketplace pages
 * @property {boolean} blogInsightsEnabled - Enable Blog/Insights pages and navigation
 * @property {boolean} letsCollaborateEnabled - Enable Let's Collaborate pages and navigation
 * @property {boolean} joinPagesEnabled - Enable join pages (consultant, integrator, collaborate) navigation
 * @property {boolean} realFormsEnabled - Enable real forms instead of LinkedIn CTAs
 */

// AWS Amplify automatically sets these environment variables
const amplifyEnvironment = process.env.REACT_APP_AWS_BRANCH || 'local';
const isProduction =
  amplifyEnvironment === 'main' || amplifyEnvironment === 'production';
const isPreview =
  amplifyEnvironment !== 'main' &&
  amplifyEnvironment !== 'production' &&
  amplifyEnvironment !== 'local';

// For testing: if we're in development but want to simulate a branch
const isDevelopment = process.env.NODE_ENV === 'development';
const simulatePreview = isDevelopment && !process.env.REACT_APP_AWS_BRANCH;

/**
 * Feature flags configuration
 * @type {FeatureFlags}
 */
export const featureFlags = {
  // Email functionality - only enabled in production
  // emailFormsEnabled: isProduction,
  emailFormsEnabled: false, // Not ready yet

  // ActiveCampaign integration - only in production
  // activeCampaignIntegration: isProduction,
  activeCampaignIntegration: false, // Not ready yet

  // Learning mode vs buy mode - preview branches show learn more, production shows buy now
  learningModeEnabled: false,

  // Debug information - only in preview/dev
  debugMode: amplifyEnvironment === 'local',

  // Preview banner - show on preview branches OR in development for testing
  previewBannerEnabled: false,

  // Google Analytics - only in production
  // googleAnalyticsEnabled: isProduction,
  googleAnalyticsEnabled: false, // Not ready yet

  // Marketplace standalone pages - enable in preview for testing
  marketplaceStandalonePagesEnabled: false, // Enable everywhere for now

  // Blog/Insights pages - enable in preview for testing
  // blogInsightsEnabled: isPreview || simulatePreview,
  blogInsightsEnabled: false, // Hidden for now

  // Let's Collaborate pages - enable in preview for testing
  // letsCollaborateEnabled: isPreview || simulatePreview,
  letsCollaborateEnabled: true, // Enabled for navigation

  // Join pages (consultant, integrator, collaborate) - separate from main Let's Collaborate
  joinPagesEnabled: false, // Hidden for now

  // Real forms - only enable in preview for testing (production uses LinkedIn CTAs for now)
  realFormsEnabled: false,
};

/**
 * Environment information
 */
export const environment = {
  name: amplifyEnvironment,
  isProduction,
  isPreview,
  isDevelopment: amplifyEnvironment === 'local',
  simulatePreview,
};

/**
 * Helper function to check if a feature is enabled
 * @param {keyof FeatureFlags} featureName - The feature flag name
 * @returns {boolean} Whether the feature is enabled
 */
export const isFeatureEnabled = (featureName) => {
  return featureFlags[featureName] || false;
};

/**
 * Helper function to get environment-specific configuration
 * @returns {Object} Environment configuration
 */
export const getEnvironmentConfig = () => ({
  apiBaseUrl: isProduction
    ? 'https://api.rsmcollective.ai'
    : 'https://api-dev.rsmcollective.ai',

  amplifyAppId: process.env.REACT_APP_AMPLIFY_APP_ID,

  // ActiveCampaign settings
  activeCampaignApiUrl: isProduction
    ? process.env.REACT_APP_AC_API_URL
    : process.env.REACT_APP_AC_DEV_API_URL,

  // Google Analytics
  googleAnalyticsId: isProduction ? process.env.REACT_APP_GA_TRACKING_ID : null,
});

// Debug logging for development
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line no-console
  console.log('🚀 Feature Flags Configuration:', {
    environment: environment.name,
    isProduction,
    isPreview,
    featureFlags,
  });
}
